import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignCreateOptionsService } from '../services';
import {
  setLoading,
  setError,
  setCreateOptions,
  setLastFetchedCompanyId,
  getCreateOptionsLastFetchedCompanyId,
} from '../slices';

const MAX_START_OPTION_ENTRIES = 1000;

export const useCampaignCreateOptionsLoader = () => {
  const dispatch = useDispatch();
  const { getCampaignCreateOptions } = useCampaignCreateOptionsService();
  const applicationId = useSelector(getApplicationSettings)?.applicationId;
  const currentCompanyId = useSelector(getCurrentCompanyId);
  const lastFetchedCompanyId = useSelector(getCreateOptionsLastFetchedCompanyId);

  // Monotonic request token. A rapid company A→B switch fires two overlapping
  // fetches; without this guard, Fetch(A) resolving after Fetch(B) would
  // overwrite the store with A's data (and lastFetchedCompanyId=A), and the
  // first-resolving finally would clear the spinner early. Only the newest
  // request (matching the latest token) is allowed to commit state.
  const requestTokenRef = useRef(0);

  const loadCreateOptions = useCallback(
    async (forceRefresh = false, companyIdOverride?: string, applicationIdOverride?: string) => {
      const companyIdToUse = companyIdOverride || currentCompanyId;
      // Callers that fetch synchronously right after selecting an application
      // (e.g. AuthContext during bootstrap, before a re-render lands the new
      // application in the store) must pass it explicitly, otherwise the
      // request would go out with a stale/undefined applicationId.
      const applicationIdToUse = applicationIdOverride ?? applicationId;

      if (!companyIdToUse) {
        return;
      }

      // A company is bound to exactly one application, so companyId alone keys
      // the cache. lastFetchedCompanyId is only set on success, so a company
      // with zero options is not treated as a cache miss on every call.
      if (!forceRefresh && lastFetchedCompanyId === companyIdToUse) {
        return;
      }

      const token = ++requestTokenRef.current;

      try {
        dispatch(setLoading(true));
        const response = await getCampaignCreateOptions({
          companyId: companyIdToUse,
          applicationIds: applicationIdToUse,
          sort: 'name_asc',
          limit: MAX_START_OPTION_ENTRIES,
          offset: 0,
        });
        // A newer request superseded this one; discard its result so the store
        // is never overwritten with stale data.
        if (token !== requestTokenRef.current) {
          return;
        }
        dispatch(setCreateOptions({ items: response.items, count: response.count }));
        dispatch(setLastFetchedCompanyId(companyIdToUse));
        dispatch(setError(null));
      } catch (error) {
        if (token !== requestTokenRef.current) {
          return;
        }
        console.error('Failed to load campaign start options:', error);
        dispatch(setError('Failed to load campaign types'));
      } finally {
        // Only the newest request clears the spinner, so an early-resolving
        // superseded request can't hide the in-flight one.
        if (token === requestTokenRef.current) {
          dispatch(setLoading(false));
        }
      }
    },
    [dispatch, getCampaignCreateOptions, applicationId, currentCompanyId, lastFetchedCompanyId],
  );

  return {
    loadCreateOptions,
  };
};
